import React, { useState, useEffect } from "react";
import SearchBar from "../components/SearchBar/SearchBar";
import Table from "../components/Table/Table";
import TopNavbar from "../components/TopNavbar/TopNavbar";
import ApiManager from "../ApiManager/ApiManager";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import CarTypes from "./CarTypes";
import { useNavigate } from "react-router-dom";
import Modal from "../components/Modal/Modal";
import ViewEntity from "../components/ViewEntity/ViewEntity";
import EditCar from "../components/CarComponents/EditCar";
import { useAuth } from "../components/AuthProvider";
import { hasPermission, ShowButton } from "../components/RoleBasedComponents";
import PERMISSIONS from "../permissions";
import { useComponentLoading } from "../utils/useComponentLoading";
import { LoadingSpinner } from "../components/LoadingSpinner/LoadingSpinner";
import { ToastContainer, toast } from 'react-toastify';

function Cars() {
  const { userPermissions } = useAuth();
  const { withLoading, isLoading, getMessage, getProgress } = useComponentLoading();
  const location = useLocation();

  const [data, setData] = useState([]);
  const [rowId, setRowId] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isDetails, setDetails] = useState(true);
  const [isEdit, setEdit] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    if (location.state?.toast) {
      const { type, message } = location.state.toast;
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'error') {
        toast.error(message);
      }
      // Clear the state to prevent showing the toast again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const fetchData = async () => {
    try {
      await withLoading('fetchCars', async (onProgress) => {
        onProgress(20, 'Fetching cars...');
        const params = new URLSearchParams({
          page_size: 12,
          page: currentPage,
        });
        const response = await ApiManager.getAllCars(params);
        onProgress(60, 'Processing data...');
        if (!response.ok) throw new Error("Failed to fetch cars");
        const json = await response.json();
        setData(json);
        setHasMorePages(json.length == 12);
        onProgress(100, 'Completed');
      });
    } catch (error) {
      console.error("Error fetching cars:", error);
      setHasMorePages(false);
    }
  };

  const navigate = useNavigate();

  const handleClick = (path) => {
    navigate(path);
  };

  const toggleModal = () => {
    setModalOpen(!isModalOpen);
  };

  const handleEdit = () => {
    navigate(`/login/cars/edit_car/${rowId}`);
  };

  const handleDetails = () => {
    console.log("Details clicked for row:");
    setDetails(true);
    setEdit(false);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleRowClick = (row) => {
    setRowId(row);
  };

  const handleFuelAnalysis = () => {
    const selectedCar = data.find((car) => car.id === rowId);
  
    if (!selectedCar) {
      alert("Please select a car row first!");
      return;
    }
    const params = new URLSearchParams({ carId: selectedCar.id});  
    navigate(`/login/cars/fuel_analysis?${params.toString()}`);
  };

  const buttons = [{ label: "Details", onClick: () => handleDetails() }];

  if (hasPermission(userPermissions, PERMISSIONS.VENDOR_CAR_UPDATE)) {
    buttons.push({ label: "Edit", onClick: () => handleEdit() });
  }

  buttons.push({ label: "Fuel Analysis", onClick: () => handleFuelAnalysis() });

  const table_columns = [
    { field: "serialNo", headerName: "S. No." },
    { field: "vehicleName", headerName: "Car Name" },
    { field: "vehicleNo", headerName: "Car Number" },
    { field: "carTypeName", headerName: "Car Type" },
    { field: "capacity", headerName: "Capacity", type: "number" },
    { field: "assignedDriverName", headerName: "Driver Name" },
  ];

  const view_columns = [
    { field: "vehicleNo", label: "Vehicle Number" },
    { field: "vehicleName", label: "Vehicle Name" },
    { field: "modelName", label: "Model Name" },
    { field: "color", label: "Color" },
    { field: "fuelType", label: "Fuel Type" },
    { field: "capacity", label: "Capacity", type: "number" },
    { field: "carTypeName", label: "Type" },
    { field: "assignedDriverName", label: "Driver Name" },
  ];

  const rows = (data || []).map((item, index) => ({
    id: item.id,
    serialNo: ((currentPage - 1) * 12) + index + 1,
    vehicleNo: item.vehicleNo,
    vehicleName: item.vehicleName,
    carTypeName: item.carTypeName || "",
    modelName: item.modelName || "",
    color: item.color || "",
    fuelType: item.fuelType || "",
    assignedDriverId: item.assignedDriverId || "",
    assignedDriverName: item.assignedDriverName || "",
    capacity: item.capacity || 0,
    type: item.type || "",
  }));

  return (
    <Routes>
      <Route
        path="/*"
        element={
          <div>
            <ToastContainer />
              <div>
                <TopNavbar
                  title="Cars"
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "right",
                    paddingTop: "20px",
                    height: "5vh",
                  }}
                >
                  <ShowButton permission={PERMISSIONS.VENDOR_CAR_CREATE}>
                    <button
                      className="custom-button"
                      onClick={() => handleClick("/login/new_car")}
                    >
                      New Car
                    </button>
                  </ShowButton>
                </div>

                <Table
                  table_columns={table_columns}
                  rows={rows}
                  setRowsId={setRowId}
                  buttons={buttons}
                  onRowClick={handleRowClick}
                  toggleModel={toggleModal}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                  hasMorePages={hasMorePages}
                  isLoading={isLoading('fetchCars')}
                  loadingMessage={getMessage('fetchCars')}
                  loadingProgress={getProgress('fetchCars')}
                />

                {isModalOpen && isDetails && (
                  <div className="details-container">
                    <Modal isOpen={true} onClose={handleModalClose}>
                      <ViewEntity
                        id={rowId}
                        fetchFunction={ApiManager.getCar}
                        columns={view_columns}
                        entity="carId"
                      />
                    </Modal>
                  </div>
                )}
              </div>
          </div>
        }
      />
      <Route path="car_types/*" element={<CarTypes />} />
    </Routes>
  );
}

export default Cars;
